import uniq from 'lodash.uniq';
import Vue from 'vue';

const DEFAULT_MODULE_STATUS = { valid: true, busy: false };

function getInitialState() {
  return {
    modules: {},
    savedModules: {},
    moduleData: {},
    moduleVars: {},
    moduleStatus: {},
    types: {},
    saving: false,
    loading: false,
    loadingModules: {}, // Keys are a eventUuids
    loadingModuleVars: {}, // Keys are a eventUuids
    dirty: [],
  };
}

export const EventModules = {
  namespaced: true,

  state: getInitialState(),

  getters: {
    getModulesForEvent: (state) => (uuid) => {
      return state.modules[uuid] ?? [];
    },

    getVarsForModule:
      ({ moduleVars }) =>
        (uuid) => {
          return moduleVars[uuid] ?? [];
        },

    isEventLoaded: (state) => (uuid) => {
      return Object.keys(state.modules).includes(uuid);
    },

    isEventDirty: (state) => (uuid) => state.dirty.includes(uuid),

    getModulesDataForEvent:
      ({ moduleData }) =>
        (subject) =>
          moduleData[subject] ?? {},

    getModuleStatus:
      ({ moduleStatus }) =>
        (moduleUuid) => {
          return moduleStatus[moduleUuid] ?? DEFAULT_MODULE_STATUS;
        },

    getModulesStatusForEvent: (_, getters) => (eventUuid) => {
      const modules = getters.getModulesForEvent(eventUuid);
      return modules
        .map(({ uuid }) => uuid)
        .reduce((result, uuid) => {
          return { ...result, [uuid]: getters.getModuleStatus(uuid) };
        }, {});
    },

    areModulesLoading:
      ({ loadingModules }) =>
        (eventUuid) =>
          !!loadingModules[eventUuid],

    areModuleVarsLoading:
      ({ loadingModuleVars }) =>
        (eventUuid) =>
          !!loadingModuleVars[eventUuid],
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    updateEventModules(state, data) {
      state.modules = { ...state.modules, ...data };
    },

    updateEventModule(state, { uuid, index, moduleData }) {
      let newModules = [...state.modules[uuid]];

      newModules[index] = { ...moduleData };

      state.modules = { ...state.modules, [uuid]: [...newModules] };
    },

    updateModuleVars(state, newVars) {
      state.moduleVars = { ...state.moduleVars, ...newVars };
    },

    setSavedEventModules(state, data) {
      state.savedModules = { ...state.savedModules, ...data };
    },

    updateEventTypes(state, types) {
      state.types = { ...types };
    },

    setEventDirty(state, uuid) {
      state.dirty = uniq([...state.dirty, uuid]);
    },

    setEventClean(state, uuid) {
      state.dirty = state.dirty.filter((item) => item !== uuid);
    },

    setModuleData(state, { subject, data }) {
      state.moduleData = { ...state.moduleData, [subject]: data };
    },

    setBusy(state, { module, status }) {
      const currentModule = state.moduleStatus[module] ?? DEFAULT_MODULE_STATUS;

      state.moduleStatus = { ...state.moduleStatus, [module]: { ...currentModule, busy: status } };
    },

    setValid(state, { module, status }) {
      const currentModule = state.moduleStatus[module] ?? DEFAULT_MODULE_STATUS;

      state.moduleStatus = { ...state.moduleStatus, [module]: { ...currentModule, valid: status } };
    },

    setLoadingModules(state, { eventUuid, value }) {
      Vue.set(state.loadingModules, eventUuid, value);
    },

    setLoadingModuleVars(state, { eventUuid, value }) {
      Vue.set(state.loadingModuleVars, eventUuid, value);
    },

    resetState(state) {
      Object.assign(state, getInitialState());
    },

    setModuleOnDoubleClick(state, { uuid, module }) {
      state.modules[uuid].push(module);
    },
  },

  actions: {
    async loadEventModules({ commit }, { event, pipeline, project, repository, version }) {
      commit('setLoading', true);
      commit('setLoadingModules', { eventUuid: event, value: true });
      try {
        const response = await this._vm.$api('repositories.projects.pipelines.events.modules.index', {
          version: '1.0',
          event,
          project,
          pipeline,
          repository,
          v: version,
        });
        commit('updateEventModules', { [event]: response.data.data });
        commit('setSavedEventModules', { [event]: response.data.data });

        const types = response.data.types;
        commit('updateEventTypes', types);
      } catch (err) {
        console.error(err);
        commit('updateEventModules', { [event]: {} });
      }

      commit('setLoading', false);
      commit('setLoadingModules', { eventUuid: event, value: false });
    },

    async loadModuleVars({ commit }, { event, pipeline, project, repository, module, version }) {
      commit('setLoading', true);
      commit('setLoadingModuleVars', { eventUuid: event, value: true });
      try {
        const response = await this._vm.$api('repositories.projects.pipelines.events.modules.vars', {
          version: '1.0',
          event,
          project,
          pipeline,
          repository,
          module,
          v: version,
        });
        commit('updateModuleVars', { [module]: response.data.data });
      } catch (err) {
        console.error(err);
        commit('updateModuleVars', { [module]: [] });
      }

      commit('setLoading', false);
      commit('setLoadingModuleVars', { eventUuid: event, value: false });
    },

    addModuleOnDoubleClick({ commit }, moduleData) {
      commit('setModuleOnDoubleClick', moduleData);
      commit('setEventDirty', moduleData.uuid);
    },

    updateEventModules({ commit }, { uuid, modules }) {
      commit('updateEventModules', { [uuid]: [...modules] });
      commit('setEventDirty', uuid);
    },

    updateEventModule({ commit }, { uuid, index, moduleData }) {
      commit('updateEventModule', { uuid, index, moduleData });
      commit('setEventDirty', uuid);
    },

    revertModuleChanges({ commit, state }, uuid) {
      commit('updateEventModules', { [uuid]: [...state.savedModules[uuid]] });
      commit('setEventClean', uuid);
    },

    async saveModuleChanges({ commit, getters }, { event, pipeline, project, repository }) {
      const saveData = getters
        .getModulesForEvent(event)
        .map(({ type, title, reference, event, settings, description, uuid }) => {
          let result = {
            type,
            uuid,
            description,
            title,
            reference,
            settings,
          };

          if (event) {
            result.event = event;
          }

          return result;
        });

      commit('setSaving', true);

      try {
        const response = await this._vm.$api(
          'repositories.projects.pipelines.events.modules.mass_save',
          { version: '1.0', event, pipeline, project, repository },
          { data: saveData }
        );

        commit('updateEventModules', { [event]: response?.data?.data });
        commit('setSavedEventModules', { [event]: response?.data?.data });
        commit('setEventClean', event);
      } catch (err) {
        console.error(err);
      }

      commit('setSaving', false);
    },

    setModuleBusy({ commit }, { module, status }) {
      commit('setBusy', { module, status });
    },

    setModuleValid({ commit }, { module, status }) {
      commit('setValid', { module, status });
    },

    resetModulesState({ commit }) {
      commit('resetState');
    },
  },
};
